import { labels } from "../../../../localization/labels";
import { messages } from "../../../../localization/messages";
import CommonUtil from "../../../../utils/CommonUtil";
import { constants } from "../../../../utils/Constants";
import TableComponentWithDialog from "../TableComponentWithDialog";

/** @class Generic table with dialog that extends TableComponent.
 *
 *Supports:
 ** filtering and sorting from TableComponent
 ** in cell editing
 ** using dialog on adding new items
 ** deleting checked items
 */
class ControlledTableComponentWithDialog extends TableComponentWithDialog {
  constructor(props) {
    super(props);

    this.inheritedData = {};
    this.allInheritedDataShouldBeDefined = false;
    this._loadedAfterAllInheritedData = false;

    this._inheritedDataChanges = this._resetChanges();
    this._inheritedDataService = this.getInheritedDataService();

    this._isCompactViewEntities = false;
  }

  /**
   * @returns {CRUDServiceOperations}
   */
  getInheritedDataService() {
    return undefined;
  }

  loadData = pageInfo => {
    if (this._inheritedDataService) {
      var inheritedData = this.getInheritedArraySafely(
        this.inheritedDataPropNames[0]
      );

      if (
        this._inheritedDataChanges[this.inheritedDataPropNames[0]] &&
        this._inheritedDataChanges[this.inheritedDataPropNames[0]].added
      ) {
        const added = this._inheritedDataChanges[this.inheritedDataPropNames[0]]
          .added;
        inheritedData = inheritedData.filter(e => {
          if (e[constants.NEW_ELEMENT_INDICATOR_PROPERTY]) {
            return false;
          }
          for (let i = 0; i < added.length; i++) {
            if (e.id === added[i].id) {
              return false;
            }
          }
          return true;
        });
      }

      pageInfo = this.addPredefinedIdsFilter(pageInfo, inheritedData, "IN");

      if (
        this._inheritedDataChanges[this.inheritedDataPropNames[0]] &&
        this._inheritedDataChanges[this.inheritedDataPropNames[0]].added
          .length > 0
      ) {
        pageInfo = this._changePageInfoWithNewElements(pageInfo);
      }

      this.prepareCall(
        this._inheritedDataService.getEntities(
          pageInfo,
          this.sortInfo,
          data => this.onDataLoad(data, pageInfo),
          this.messageHandler.handleError,
          this._isCompactViewEntities
        )
      );
      this.performCalls();
    } else {
      throw new Error(
        "You have to implement the method loadData or implement the method getInheritedDataService!"
      );
    }
  };

  _isShiftedPage = (page, size, shift) => {
    const nextOffset = (page + 1) * size;
    return nextOffset < shift;
  };

  _isHalfShiftedPage = (page, size, shift) => {
    const currOffset = page * size;
    const nextOffset = (page + 1) * size;
    return shift >= currOffset && shift <= nextOffset;
  };

  _calculatePage = (page, size, shift) => {
    const shifted = this._isShiftedPage(page, size, shift);
    const halfShifted = this._isHalfShiftedPage(page, size, shift);

    if (shifted || halfShifted) {
      return 0;
    } else {
      size = size === 0 ? 1 : size;
      return page - Math.ceil(shift / size);
    }
  };

  _calculateSize = (page, size, shift) => {
    const shifted = this._isShiftedPage(page, size, shift);
    const halfShifted = this._isHalfShiftedPage(page, size, shift);

    if (shifted) {
      return 0;
    } else if (halfShifted) {
      const nextOffset = (page + 1) * size;
      return nextOffset - shift;
    } else {
      return size;
    }
  };

  _calculateOffset = (page, size, shift) => {
    const shifted = this._isShiftedPage(page, size, shift);
    const halfShifted = this._isHalfShiftedPage(page, size, shift);

    const currOffset = page * size;

    if (!shifted && !halfShifted) {
      size = size === 0 ? 1 : size;
      return currOffset - shift;
    } else if (!shifted) {
      return 0;
    } else {
      return currOffset;
    }
  };

  _changePageInfoWithNewElements = pageInfo => {
    const shift = this._inheritedDataChanges[this.inheritedDataPropNames[0]]
      .added.length;
    const page = pageInfo.number;
    const size = pageInfo.size;

    const shiftedPage = this._calculatePage(page, size, shift);
    const shiftedSize = this._calculateSize(page, size, shift);
    const shiftedOffset = this._calculateOffset(page, size, shift);

    pageInfo = {
      ...pageInfo,
      number: shiftedPage,
      size: shiftedSize === 0 ? 1 : shiftedSize,
      offset: shiftedOffset,
      _empty: shiftedSize === 0
    };

    return pageInfo;
  };

  onDataLoad = (data, requestedPageInfo) => {
    if (
      this._inheritedDataService &&
      this._inheritedDataChanges[this.inheritedDataPropNames[0]] &&
      (this._inheritedDataChanges[this.inheritedDataPropNames[0]].added.length >
        0 ||
        this._inheritedDataChanges[this.inheritedDataPropNames[0]].changed
          .length > 0)
    ) {
      const pageInfo = this._calculatePageInfoAfterDataLoad(data);

      var content =
        !data._embedded || requestedPageInfo._empty
          ? []
          : data._embedded.content;
      content = this._addNewElements(content, pageInfo);
      this._replaceChangedElements(content);

      this.setState({
        content: content,
        pageInfo: pageInfo,
        links: data._links,
        isLoading: false,
        showTable: true
      });
    } else {
      super.onDataLoad(data);
    }

    this._loadAfterArrayDataLoaded();
  };

  _loadAfterArrayDataLoaded = () => {
    //do nothing here
  };

  _calculatePageInfoAfterDataLoad = data => {
    const totalElements =
      data.page.totalElements +
      this._inheritedDataChanges[this.inheritedDataPropNames[0]].added.length;

    let pageInfo = {
      ...data.page,
      size: this.state.pageInfo.size,
      number: this.state.pageInfo.number,
      totalElements: totalElements,
      totalPages: Math.ceil(totalElements / this.state.pageInfo.size),
      filters: this.state.pageInfo.filters
    };

    return pageInfo;
  };

  _addNewElements = (existing, pageInfo) => {
    const newElementsNumber = this._inheritedDataChanges[
      this.inheritedDataPropNames[0]
    ].added.length;

    const prevElementsNumber = pageInfo.size * pageInfo.number;
    const newElementsStartingIndex =
      newElementsNumber - prevElementsNumber < 0
        ? newElementsNumber
        : prevElementsNumber;

    const nextElementsNumber = pageInfo.size * (pageInfo.number + 1);
    const elementsDiff =
      newElementsNumber - nextElementsNumber > 0
        ? 0
        : newElementsNumber - nextElementsNumber;
    const newElementsEndingIndex =
      newElementsStartingIndex === newElementsNumber
        ? 0
        : elementsDiff <= pageInfo.size
        ? newElementsStartingIndex + pageInfo.size - elementsDiff
        : newElementsStartingIndex + pageInfo.size;

    return CommonUtil.joinArrays(
      this._inheritedDataChanges[this.inheritedDataPropNames[0]].added,
      existing,
      newElementsStartingIndex,
      newElementsEndingIndex
    );
  };

  _replaceChangedElements = existing => {
    this._inheritedDataChanges[this.inheritedDataPropNames[0]].changed.forEach(
      e => {
        //here Array.filter(...) must not be used because it creates new array
        for (let i = 0; i < existing.length; i++) {
          if (existing[i].id === e.id) {
            existing[i] = { ...existing[i] };
            CommonUtil.replaceObjectProperties(existing[i], e, [
              "id",
              constants.NEW_ELEMENT_INDICATOR_PROPERTY
            ]);
          }
        }
      }
    );
  };

  _resetChanges() {
    var changes = { _newIdCounter: -1 };
    if (this.inheritedDataPropNames) {
      for (let i = 0; i < this.inheritedDataPropNames.length; i++) {
        changes[this.inheritedDataPropNames[i]] = {
          added: [],
          deleted: [],
          changed: []
        };
      }
    }

    return changes;
  }

  inheritedDataPropNames = [];

  getInheritedArraySafely = propName => {
    return this.getInheritedObject(propName, []);
  };
  getInheritedObjectSafely = propName => {
    return this.getInheritedObject(propName, {});
  };

  getInheritedObject = (propName, unexistingValue = undefined) => {
    var inheritedObject = CommonUtil.getDeep(this.inheritedData, propName);
    inheritedObject =
      inheritedObject === undefined ? unexistingValue : inheritedObject;
    return inheritedObject;
  };

  componentDidUpdate() {
    var inhertitedDataPropsChanged = false;
    var inhertitedDataPropsAllDefined = true;

    const inheritedPropNames = [...this.inheritedDataPropNames, "reset"];

    for (var i = 0; i < inheritedPropNames.length; i++) {
      const propName = inheritedPropNames[i];
      var newVal = CommonUtil.getDeep(this.inheritedData, propName);

      const isInheritedPropChanged = !CommonUtil.objectsHasSameProperty(
        this.inheritedData,
        this.props,
        propName
      );

      if (isInheritedPropChanged) {
        newVal = CommonUtil.getDeep(this.props, propName);
        CommonUtil.setDeepOnStringPath(this.inheritedData, propName, newVal);
        inhertitedDataPropsChanged = true;

        if (propName === "reset" && newVal === true) {
          this._inheritedDataChanges = this._resetChanges();
          this.resetData();

          const wasReset = this.loadAfterReset();
          if (!wasReset) {
            CommonUtil.setDeepOnStringPath(this.inheritedData, "reset", false);
            inhertitedDataPropsChanged = false;
          }
        }
      }
      if (
        newVal === undefined /*|| newVal.length === 0*/ &&
        propName !== "reset"
      ) {
        inhertitedDataPropsAllDefined = false;
      }
    }

    if (inhertitedDataPropsChanged) {
      if (
        !this.allInheritedDataShouldBeDefined ||
        (this.allInheritedDataShouldBeDefined && inhertitedDataPropsAllDefined)
      ) {
        if (!this._loadedAfterAllInheritedData) {
          this.loadAfterAllInheritedData();
          this._loadedAfterAllInheritedData = true;
        }

        this.startLoading(this.state.pageInfo, this.sortInfo);
      }
    } else {
      super.componentDidUpdate();
    }
  }

  loadAfterAllInheritedData() {
    //do nothing here
  }

  resetData() {
    //do nothing here
  }

  loadAfterReset() {
    //do nothing here
    return true;
  }

  onCreateData = entity => {
    if (this.props.addToTable) {
      if (!entity.id) {
        entity.id = this._inheritedDataChanges._newIdCounter--;
      }
      entity[constants.NEW_ELEMENT_INDICATOR_PROPERTY] = true;

      this._inheritedDataChanges[this.inheritedDataPropNames[0]].added.push(
        entity
      );

      if (this.props.addToTable(entity)) {
        // this.startLoading(this.state.pageInfo, this.sortInfo);
        this.handleClose();
      } else {
        this._inheritedDataChanges[this.inheritedDataPropNames[0]].added.pop();
        this.messageHandler.handleWarning({
          text: messages.existingElementAdditionWarning
        });
      }
    } else {
      throw new Error("You have to implement the method onCreateData!");
    }
  };

  onDeleteData = entity => {
    if (this.props.removeFromTable) {
      this._inheritedDataChanges[this.inheritedDataPropNames[0]].deleted.push(
        entity
      );

      if (entity[constants.NEW_ELEMENT_INDICATOR_PROPERTY]) {
        CommonUtil.removeElementFromArray(
          this._inheritedDataChanges[this.inheritedDataPropNames[0]].added,
          entity
        );
      }

      this.props.removeFromTable(entity);

      // this.startLoading(this.state.pageInfo, this.sortInfo);
    } else {
      throw new Error("You have to implement the method onDeleteData!");
    }
  };

  onDataResponse = data => {};

  loadSelectedData = url => {
    this.prepareCall(
      this.getInheritedDataService().getEntitiesByUrl(
        url,
        this.onSelectedEntityLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  updateData = entity => {
    if (this.props.updateInTable) {
      this._inheritedDataChanges[this.inheritedDataPropNames[0]].changed.push(
        entity
      );

      if (this.props.updateInTable(entity)) {
        // this.startLoading(this.state.pageInfo, this.sortInfo);
        this.handleClose();
      } else {
        this._inheritedDataChanges[
          this.inheritedDataPropNames[0]
        ].changed.pop();
        this.messageHandler.handleWarning({
          text: messages.existingElementAdditionWarning
        });
      }
    } else {
      throw new Error("You have to implement the method updateData!");
    }
  };

  onRowClick = (_, row) => {
    this.onSelectedEntityLoad(row);
  };

  handleDialogChange = event => {
    let { newEntity } = this.state;
    const path = event?.target?.id;
    if (path) {
      CommonUtil.setDeepOnStringPath(newEntity, path, event.target.value);
    } else {
      newEntity = event?.target?.value || event;
    }
    const disabled = !this.validator(newEntity);
    this.setState({ newEntity: newEntity, disabled: disabled });
  };

  renderProps() {
    return {
      ...super.renderProps(),
      saveButtonText: this.props.addToTable ? labels.addButton : undefined
    };
  }
}

export default ControlledTableComponentWithDialog;
